import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [proxyHelp, setProxyHelp] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setProxyHelp(false);
    setLoading(true);

    try {
      await login({ email, password });
      navigate('/dashboard');
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Erro ao fazer login';
      const isNetworkErr = err.code === 'ERR_NETWORK' || msg.includes('Network Error');
      const detail = err.response?.status === 401 ? '' : isNetworkErr
        ? ' Confira: (1) Abra https://api.vettorchat.com.br/health no navegador; (2) Backend com CLIENT_URL=https://vettorchat.com.br; (3) SSL do api válido.'
        : '';
      setError(msg + detail);
      setProxyHelp(msg.includes('PROXY_ERR') || msg.includes('subdomínio') || msg.includes('frontend em vez do backend') || isNetworkErr);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)',
        p: 2,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(212, 175, 55, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(212, 175, 55, 0.1) 0%, transparent 50%)',
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={24}
          sx={{
            p: 4,
            borderRadius: 3,
            backgroundColor: 'white',
            border: '1px solid rgba(212, 175, 55, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(212, 175, 55, 0.1)',
          }}
        >
          {/* Logo Section */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#1a1a1a',
                fontWeight: 'bold',
                fontSize: '2rem',
                mx: 'auto',
                mb: 2,
                boxShadow: '0 4px 12px rgba(212, 175, 55, 0.4)',
              }}
            >
              VP
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a1a1a', mb: 1 }}>
              VETTORPRINT
            </Typography>
            <Typography variant="h6" sx={{ color: '#D4AF37', fontWeight: 600 }}>
              Sistema CRM
            </Typography>
          </Box>

          <Card sx={{ boxShadow: 'none', backgroundColor: 'transparent' }}>
            <CardContent sx={{ p: 0 }}>
              <Typography variant="h5" sx={{ mb: 3, textAlign: 'center', fontWeight: 600 }}>
                Entrar no Sistema
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setError(''); setProxyHelp(false); }}>
                  {error}
                </Alert>
              )}

              {proxyHelp && (
                <Alert severity="info" sx={{ mb: 2 }} icon={false}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Se for Network Error (API inacessível):</Typography>
                  <Typography component="ol" variant="body2" sx={{ pl: 2, m: 0, '& li': { mb: 0.5 } }}>
                    <li>Abra <strong>https://api.vettorchat.com.br/health</strong> no navegador. Deve retornar JSON <code>{"{ \"status\": \"OK\" }"}</code>.</li>
                    <li><strong>Backend</strong> (Easypanel): variável <code>CLIENT_URL</code> = <code>https://vettorchat.com.br</code>.</li>
                    <li><strong>Frontend</strong>: variável <code>REACT_APP_API_URL</code> = <code>https://api.vettorchat.com.br/api</code> e redeploy.</li>
                    <li>Se /health não abrir: confira DNS (api → 72.60.248.113), domínio no backend (api.vettorchat.com.br:5000) e SSL.</li>
                  </Typography>
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin="normal"
                  required
                  autoComplete="email"
                  autoFocus
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Senha"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  required
                  autoComplete="current-password"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    mt: 3,
                    mb: 2,
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Entrar'
                  )}
                </Button>
              </Box>

              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Sistema de gestão VettorPrint
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
